import { useEffect, useMemo, useState } from "react";
import { Home, type Recent } from "./routes/Home";
import { LookUpView } from "./lookup/LookUpView";
import { useLookUp } from "./lookup/useLookUp";
import { StoryView } from "./story/StoryView";
import { useStory } from "./story/useStory";
import { ShareButton } from "./sharing/ShareButton";
import type { Id } from "../../../convex/_generated/dataModel";

const RECENTS_KEY = "no-website-recents";
const RECENTS_MAX = 6;

function readRecents(): Recent[] {
  try {
    const raw = window.localStorage.getItem(RECENTS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as Recent[];
    return Array.isArray(parsed) ? parsed.slice(0, RECENTS_MAX) : [];
  } catch {
    return [];
  }
}

function pushRecent(entry: Recent) {
  try {
    const cur = readRecents().filter((r) => r.q.toLowerCase() !== entry.q.toLowerCase());
    const next = [entry, ...cur].slice(0, RECENTS_MAX);
    window.localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
}

function getOrCreateSessionId(): string {
  const k = "no-website-sid";
  const ex = window.localStorage.getItem(k);
  if (ex) return ex;
  const sid = crypto.randomUUID();
  window.localStorage.setItem(k, sid);
  return sid;
}

function readQueryFromUrl(): string | null {
  if (typeof window === "undefined") return null;
  const q = new URL(window.location.href).searchParams.get("q");
  return q && q.trim() ? q : null;
}

export function App() {
  const sessionId = useMemo(getOrCreateSessionId, []);
  const [query, setQueryState] = useState<string | null>(readQueryFromUrl);
  const [showStory, setShowStory] = useState<boolean>(() =>
    typeof window !== "undefined" && new URL(window.location.href).searchParams.get("s") === "1",
  );

  function setQuery(q: string | null) {
    setQueryState(q);
    if (typeof window !== "undefined") {
      const url = new URL(window.location.href);
      if (q) url.searchParams.set("q", q);
      else url.searchParams.delete("q");
      url.searchParams.delete("s");
      window.history.replaceState(null, "", url.toString());
    }
    setShowStory(false);
  }

  function openStory() {
    setShowStory(true);
    if (typeof window !== "undefined") {
      const url = new URL(window.location.href);
      url.searchParams.set("s", "1");
      window.history.replaceState(null, "", url.toString());
    }
  }

  useEffect(() => {
    function onPop() {
      setQueryState(readQueryFromUrl());
      setShowStory(new URL(window.location.href).searchParams.get("s") === "1");
    }
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const lookup = useLookUp(query, sessionId);
  const entityId =
    lookup.envelope?.kind === "answer" ? lookup.envelope.result.canonicalId : null;
  const story = useStory(showStory ? entityId : null);

  useEffect(() => {
    if (!query || lookup.status !== "ready" || !lookup.envelope) return;
    if (lookup.envelope.kind !== "answer") return;
    const r = lookup.envelope.result;
    pushRecent({
      q: query,
      title: r.title,
      thumbUrl: r.mediaPreview.kind === "image" ? r.mediaPreview.url : undefined,
    });
  }, [query, lookup.status, lookup.envelope]);

  if (!query) return <Home onSearch={setQuery} recents={readRecents()} />;
  if (lookup.status === "loading")
    return <p lang="en" className="app-status">Looking up…</p>;
  if (lookup.status === "error" || !lookup.envelope)
    return <p lang="en" role="alert" className="app-status">Something went wrong.</p>;
  return (
    <>
      <LookUpView envelope={lookup.envelope} onOpenStory={openStory} />
      {showStory && (
        <>
          <StoryView story={story ?? undefined} />
          {story && story.status === "ready" && story._id && (
            <ShareButton
              storyId={story._id as Id<"stories">}
              sessionId={sessionId}
              covers={story.covers ?? []}
            />
          )}
        </>
      )}
    </>
  );
}
